import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentReturned from '@mui/icons-material/AssignmentReturned';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import ClearAll from '@mui/icons-material/ClearAll';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Delete from '@mui/icons-material/Delete';
import Merge from '@mui/icons-material/MergeType';
import PendingActions from '@mui/icons-material/PendingActions';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Send from '@mui/icons-material/Send';
import { Button, Divider, MenuItem } from '@mui/material';
import { Table } from '@tanstack/react-table';
import { Features, OrderStatus, PermissionType } from 'harmony-constants';
import { L } from 'harmony-language';
import React from 'react';
import { useMergeDraftsMutation } from '../../api/mutations/reworked/use-merge-drafts-mutation';
import { useCarriers } from '../../api/queries/use-carriers';
import { useCompanyId } from '../../app/agistics-context';
import { Load } from '../../types';
import { ordersToCommonStopLocationIds } from '../../utils/data-mapping-utils';
import { downloadDeliveryTickets, downloadLoadsCSV } from '../../utils/download-utils';
import { useWeights } from '../shared/hooks/use-weights';
import { MenuButton } from '../shared/inputs/menu-button';
import LoadingButton from '../shared/loading-button';
import { useCarrier, useFeatures, usePermissions } from '../user/selectors/use-permissions';
import { DraftsLoadsModal } from './drafts-loads';
import { useDraftsLoadsContext } from './drafts-loads-context';
import { useMergeDrafts } from './drafts/merge/use-merge-drafts';
import { isDraftValid } from './drafts/utils/validate-drafts';
import { RunningTime } from './table-components/running-time';
import { setExpandedRows } from './utils/drafts-loads-utils';

// OLD NOTES
// calling getSelectedRowModel while using <- -> buttons causes infinite loop.
// unsure if this is because something getSelectedRowModel is doing or something not wired up in react-query properly
// still not sure what causes this, but this still needs to be memoized...
// const selectedLoads = useMemo(() => {
//     console.log('useMemo!');
//     return table.getSelectedRowModel().rows.map(x => x.original);
// }, [table.getState().rowSelection, table]);

// after not touching this for 2 weeks, it no longer needs to be useMemo I guess
// if it is memo and a load is invalid to be published, you edit it so it is valid
// it will still disable to publish button...
// still infinite loop when changing from drafts -> loads if not useMemo

// NEW NOTES
// calling getSelectedRowModel() while loading new (loads/drafts) data seams to be what causes the infinite loop
// just doing an empty return while loading fixes it.  We are resetting selection state and not showing this component
// so will not affect visibility / usability anyways.
// a reason we can't memo it:
//      On drafts - Have 1+ rows selected. Edit it so it is no longer in a 'valid for publish' state.
//      Previously it would have shown that load as 'valid for publish' as getSelectedRowModel() was memoized

interface SelectionButtonsProps {
    isLoading: boolean;
    table: Table<Load>;
    setDialogOpen: (s: DraftsLoadsModal) => void;
    rowSelectionOrder: number[];
}

export const SelectionButtons: React.FC<SelectionButtonsProps> = (props) => {
    const { isLoading, table, setDialogOpen, rowSelectionOrder } = props;
    const { isDrafts, getIsLoadStale } = useDraftsLoadsContext();
    const companyId = useCompanyId();
    const { weightSystem, convertFromGrams, weightPrecision } = useWeights();
    const hasDeliveryTicketsFeature = useFeatures(Features.DeliveryTickets);
    const hasMergeDraftsFeature = useFeatures(Features.MergeDrafts);
    const hasCascadeFeature = useFeatures(Features.CascadingStopScheduling);
    const hasBulkSchedulingFeature = useFeatures(Features.BulkScheduling);

    const canCompleteOrder = usePermissions(PermissionType.CompleteOrder);
    const canAssignDrivers = usePermissions(PermissionType.AssignDrivers);
    const canDeleteOrders = usePermissions(PermissionType.DeleteOrder);

    const currentUserIsCarrierOrg = useCarrier();
    const { carriers } = useCarriers();

    const [isExportingLoads, setIsExportingLoads] = React.useState(false);

    // read above for more info on this isLoading check
    if (isLoading) {
        return <></>;
    }

    const hasRows = table.getFilteredRowModel().rows.length > 0;
    const selectedLoads = table.getSelectedRowModel().rows.map(x => x.original);
    const hasLoadsSelected = Boolean(selectedLoads.length);
    const orderedSelectedLoads = rowSelectionOrder.map(x => {
        const load = selectedLoads.find(selectedLoad => selectedLoad.id === x);
        return load;
    }).filter(x => !!x);

    const anySelectedLoadsDelivered = selectedLoads.some(x => x.status === OrderStatus.Delivered);
    const allSelectedOpenOrAssigned = selectedLoads.every(x => x.status === OrderStatus.Open || x.status === OrderStatus.Assigned);
    const anySelectedLoadIsStale = selectedLoads.some(load => getIsLoadStale(load));
    const sameOrg = selectedLoads.every(x => x.transportingOrganizationId === companyId);

    return (
        hasRows && <div className='lt-selection-buttons-container'>
            <div className='lt-selection-buttons'>
                <Divider orientation='vertical' />
                {!isDrafts && <LoadingButton
                    variant='contained'
                    icon={<CloudDownloadIcon />}
                    isLoading={isExportingLoads}
                    onClick={() => {
                        setIsExportingLoads(true);
                        const loadIds = table.getFilteredRowModel().rows.map(x => x.id)
                        downloadLoadsCSV(companyId, weightSystem, loadIds, setIsExportingLoads);
                    }}
                >
                    {L.exportData()}
                </LoadingButton>}
                {hasLoadsSelected && isDrafts && hasMergeDraftsFeature && <MenuButton
                    label={L.merge()}
                    ButtonProps={{
                        variant: 'contained',
                        startIcon: <Merge style={{ transform: 'rotate(90deg)' }} />,
                        disabled: selectedLoads.length <= 1 || anySelectedLoadIsStale, // TODO test
                    }}
                >
                    <MenuItem onClick={() => setDialogOpen('mergeDrafts')}>{L.manual()}</MenuItem>
                    <QuickMergeMenuItem drafts={orderedSelectedLoads} table={table} />
                    <MenuItem onClick={() => setDialogOpen('autoMergeDrafts')}>{L.auto()}</MenuItem>
                </MenuButton>}
                {hasLoadsSelected && isDrafts && !currentUserIsCarrierOrg && hasBulkSchedulingFeature && <Button
                    variant='contained'
                    startIcon={<PendingActions />}
                    disabled={anySelectedLoadIsStale}
                    onClick={() => setDialogOpen('bulkScheduleDrafts')}
                >
                    {L.schedule()}
                </Button>}
                {hasLoadsSelected && isDrafts && <Button
                    variant='contained'
                    startIcon={<AutoFixHigh />}
                    disabled={anySelectedLoadIsStale}
                    onClick={() => setDialogOpen('bulkModifyDrafts')}
                >
                    {L.bulkModify()}
                </Button>}
                {hasLoadsSelected && isDrafts && hasCascadeFeature && <Button
                    variant='contained'
                    startIcon={<ClearAll style={{ transform: 'scaleX(-1)' }} />}
                    onClick={() => setDialogOpen('cascadeDrafts')}
                    disabled={!(selectedLoads.length > 1 && ordersToCommonStopLocationIds(selectedLoads).length) || anySelectedLoadIsStale}
                >
                    {L.cascade()}
                </Button>}
                {hasLoadsSelected && !isDrafts && hasDeliveryTicketsFeature && <Button
                    variant='contained'
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => {
                        downloadDeliveryTickets(selectedLoads.map(x => x.id), weightSystem);
                    }}
                    disabled={!selectedLoads.every(sl => sl.stops.some(s => s.hasDeliveryTicket))}
                >
                    {L.deliveryTicket()}
                </Button>}
                {hasLoadsSelected && !isDrafts && !currentUserIsCarrierOrg && canCompleteOrder && <Button
                    variant='contained'
                    startIcon={<AssignmentTurnedIn />}
                    onClick={() => setDialogOpen('completeLoads')}
                    disabled={anySelectedLoadsDelivered || anySelectedLoadIsStale}
                >
                    {L.manuallyCompleteLoad()}
                </Button>}
                {hasLoadsSelected && canAssignDrivers && <Button
                    variant='contained'
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => setDialogOpen('assignLoads')}
                    disabled={anySelectedLoadsDelivered || !sameOrg || anySelectedLoadIsStale}
                >
                    {L.assignDriver()}
                </Button>}
                {hasLoadsSelected && isDrafts && <Button
                    variant='contained'
                    startIcon={<Send />}
                    onClick={() => setDialogOpen('publishDrafts')}
                    disabled={!selectedLoads.every(x => isDraftValid(x, convertFromGrams, weightPrecision)) || anySelectedLoadIsStale}
                >
                    {L.publish()}
                </Button>}
                {hasLoadsSelected && !isDrafts && !currentUserIsCarrierOrg && carriers && carriers.length > 0 && <Button
                    variant='contained'
                    startIcon={<AssignmentReturned />}
                    onClick={() => setDialogOpen('assignCarrier')}
                    disabled={anySelectedLoadsDelivered || anySelectedLoadIsStale}
                >
                    {L.assignCarrier()}
                </Button>}
                {hasLoadsSelected && <RunningTime selectedLoads={orderedSelectedLoads} />}
            </div>
            <div>
                {hasLoadsSelected && !currentUserIsCarrierOrg && canDeleteOrders && <Button
                    variant='contained'
                    startIcon={<Delete />}
                    onClick={() => setDialogOpen('deleteRecords')}
                    disabled={!allSelectedOpenOrAssigned || anySelectedLoadIsStale}
                    color='error'
                >
                    {L.delete()}
                </Button>}
            </div>
        </div>
    );
};

const QuickMergeMenuItem = (props: { drafts: Load[], table: Table<Load> }) => {
    const { drafts, table } = props;
    const { mutate: mergeDrafts } = useMergeDraftsMutation();
    const mergedDraft = useMergeDrafts(drafts);

    function quickMergeDrafts() {
        mergeDrafts({ drafts: [mergedDraft] }, {
            onSuccess: (mergedDrafts: Load[]) => {
                table.resetExpanded(true);
                setExpandedRows(table, mergedDrafts.map(d => d.id));
            }
        });
    }

    return (
        <MenuItem onClick={() => quickMergeDrafts()}>{L.quick()}</MenuItem>
    )
}